import { Combobox } from '@headlessui/react'
import { useEffect, useState } from 'react'

import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'

import MapIcon from '../../../../public/svg/find-an-office.svg'
import styles from './Officeinputsearch.module.css'

interface OfficeResult {
  id: string
  name: string
  postcode?: string
  suburb?: string
  office: string
  url: string
}

interface StoryblokStory {
  id: string
  name: string
  full_slug: string
  content: {
    component: string
    office?: Array<{ address?: string }>
    postcode?: string
    suburb?: string
    city?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface StoryblokResponse {
  stories: StoryblokStory[]
}

export type OfficeinputsearchProps = {
  placeholder?: string
  width?: string | null
  variant?: 'primary' | 'secondary'
  hardCodedResults?: OfficeResult[]
  onSelect?: (item: OfficeResult) => void
  selectValue?: string
  onReset?: () => void
}

function PhnxOfficeinputsearch({
  placeholder = 'Enter a postcode or city name',
  width = '100%',
  variant = 'primary',
  hardCodedResults = [],
  onSelect,
}: OfficeinputsearchProps) {
  const [results, setResults] = useState<OfficeResult[]>([])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedOffice, setSelectedOffice] = useState<OfficeResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  // Debounce the query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  // Fetch offices based on search term
  useEffect(() => {
    if (hardCodedResults.length > 0) {
      setResults(hardCodedResults)
      return
    }

    if (!debouncedQuery.trim() || debouncedQuery.trim().length < 2) {
      setResults([])
      return
    }

    const searchOffices = async () => {
      try {
        setIsLoading(true)
        const location = process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au'

        const sbParams = {
          ...searchParamsData,
          search_term: debouncedQuery,
          filter_query: {
            'settings.location_available': { in: location },
            __or: [{ component: { in: 'advanceOffice' } }, { component: { in: 'office' } }],
          },
          sort_by: 'content.title:asc',
          per_page: 100,
        }

        const storyblokApi = getStoryblokApi()
        const { data }: { data: StoryblokResponse } = await storyblokApi.get(
          `cdn/stories/`,
          sbParams
        )

        const searchResults: OfficeResult[] =
          data?.stories != null
            ? data.stories
                .map((story): OfficeResult | null => {
                  let postcode = null
                  let suburb = null

                  if (story.content.component === 'advanceOffice') {
                    const address = story.content.office?.[0]?.address ?? ''
                    const match = address.match(/(?:VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\s+(\d{4})/i)
                    postcode = match?.[1] ?? null

                    // Extract suburb from address (usually the part before the state)
                    const suburbMatch = address.match(/^([^,]+)/)
                    suburb = suburbMatch?.[1]?.trim() ?? null
                  } else {
                    postcode = story.content.postcode
                    suburb = story.content.suburb ?? story.content.city ?? null
                  }

                  if (postcode == null) {
                    return null
                  }

                  return {
                    id: story.id,
                    name: `${story.name}, ${postcode}`,
                    postcode,
                    suburb: suburb ?? undefined,
                    office: story.name,
                    url: '/' + story.full_slug,
                  }
                })
                .filter((item): item is OfficeResult => item !== null)
                // Additional client-side filtering for better postcode/suburb matching
                .filter((office) => {
                  const queryLower = debouncedQuery.toLowerCase()
                  const postcodeLower = office.postcode?.toLowerCase() ?? ''
                  const suburbLower = office.suburb?.toLowerCase() ?? ''
                  const nameLower = office.name.toLowerCase()
                  const officeLower = office.office.toLowerCase()

                  return (
                    postcodeLower.includes(queryLower) ||
                    suburbLower.includes(queryLower) ||
                    nameLower.includes(queryLower) ||
                    officeLower.includes(queryLower)
                  )
                })
                .sort((a, b) => {
                  const queryLower = debouncedQuery.toLowerCase()
                  const aPostcodeLower = a.postcode?.toLowerCase() ?? ''
                  const bPostcodeLower = b.postcode?.toLowerCase() ?? ''
                  const aSuburbLower = a.suburb?.toLowerCase() ?? ''
                  const bSuburbLower = b.suburb?.toLowerCase() ?? ''

                  // Exact postcode match gets highest priority
                  if (aPostcodeLower === queryLower && bPostcodeLower !== queryLower) return -1
                  if (bPostcodeLower === queryLower && aPostcodeLower !== queryLower) return 1

                  // Then postcode starts with query
                  if (
                    aPostcodeLower.startsWith(queryLower) &&
                    !bPostcodeLower.startsWith(queryLower)
                  )
                    return -1
                  if (
                    bPostcodeLower.startsWith(queryLower) &&
                    !aPostcodeLower.startsWith(queryLower)
                  )
                    return 1

                  // Then suburb starts with query
                  if (aSuburbLower.startsWith(queryLower) && !bSuburbLower.startsWith(queryLower))
                    return -1
                  if (bSuburbLower.startsWith(queryLower) && !aSuburbLower.startsWith(queryLower))
                    return 1

                  // Otherwise alphabetical order by name
                  return a.name.localeCompare(b.name)
                })
                .slice(0, 25)
            : []
        setResults(searchResults)
      } catch (error) {
        console.error('Error searching offices:', error)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }

    searchOffices()
  }, [debouncedQuery])

  const handleOnSelect = (item: OfficeResult | null) => {
    if (item && onSelect) {
      onSelect(item)
    } else if (item) {
      window.location.href = item.url
    }
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setSelectedOffice(null)
  }

  // Clear when selected office changes (when clicking off or selecting)
  useEffect(() => {
    if (selectedOffice) {
      setQuery('')
      setResults([])
    }
  }, [selectedOffice])

  return (
    <div className={`${styles['officeinputsearch']} ${styles[`variant-${variant}`]}`}>
      <div style={{ width: width ?? '100%' }} className="relative">
        <Combobox
          value={selectedOffice}
          onChange={(value) => {
            setSelectedOffice(value)
            handleOnSelect(value)
          }}
        >
          <div className="relative">
            <Combobox.Input
              className={styles.search}
              placeholder={placeholder}
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(office: OfficeResult | null) => office?.name ?? ''}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {query.trim() && (
              <Combobox.Options className={styles.options}>
                {isLoading ? (
                  <div className={styles.option}>
                    <span className={styles.result}>Searching...</span>
                  </div>
                ) : results.length > 0 ? (
                  results.map((item) => (
                    <Combobox.Option
                      key={item.id}
                      value={item}
                      className={`${styles.option} data-[selected]:${styles.optionActive}`}
                    >
                      <span className={styles.result}>
                        <MapIcon />
                        {item.name}
                      </span>
                    </Combobox.Option>
                  ))
                ) : (
                  <div className={styles.option}>
                    <span className={styles.result}>No offices found</span>
                  </div>
                )}
              </Combobox.Options>
            )}
          </div>
        </Combobox>
        {query.trim() && isFocused && (
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer hover:opacity-70"
            onClick={handleClear}
            style={{ zIndex: 1000 }}
            aria-label="Clear search"
          >
            <svg
              width="20"
              height="20"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              style={{ fill: variant === 'secondary' ? 'black' : 'white' }}
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

export { PhnxOfficeinputsearch }
export default PhnxOfficeinputsearch
